/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import {
    IBaseRequestPromise,
    ObjectTypeItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';

import { withFullModalMixin } from 'ajs/js/utilities/mixins';

import {
    AviPermissionResource,
    ICluster,
} from 'generated-types';

import { Cluster } from 'object-types';

import { withEditChildMessageItemMixin }
    from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { IpAddrConfigItem } from 'ajs/modules/data-model/factories/ip-addr.config-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { ClusterNodeConfigItem } from 'message-items/cluster-node.config-item.factory';

type TClusterConfigPartial = Omit<ICluster, 'nodes' | 'virtual_ip' | 'virtual_ip6'>;

export interface IExtendedClusterConfig extends TClusterConfigPartial {
    nodes?: RepeatedMessageItem<ClusterNodeConfigItem>;
    virtual_ip?: IpAddrConfigItem;
    virtual_ip6?: IpAddrConfigItem;
    managed_object_id?: string;
}

interface IClusterData {
    config: IExtendedClusterConfig;
}

/**
 * @description Cluster Item class.
 * @author Ratan Kumar
 */
export class ClusterItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    public data: IClusterData;
    public getConfig: () => IExtendedClusterConfig;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'cluster',
            objectType: Cluster,
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_CONTROLLER,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Returns a API URI to save an object.
     * @override
     */
    public urlToSave(): string {
        return `/api/${this.objectName}?include_name`;
    }

    /**
     * Getter to get nodes from cluster config.
     */
    public get clusterNodeList(): RepeatedMessageItem<ClusterNodeConfigItem> {
        const { nodes } = this.config;

        return nodes;
    }

    /**
     * getter to get virtual ip.
     */
    public get virtualIp(): IpAddrConfigItem {
        return this.config.virtual_ip;
    }

    /**
     * Return Virtual IPv6 address message item.
     */
    public get virtualIp6(): IpAddrConfigItem {
        return this.config.virtual_ip6;
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public getIdFromData_(): string {
        return 'default'; // fakeId for single instance
    }

    /**
     * Method to add new cluster node item.
     */
    public addClusterNode(): void {
        this.addChildMessageItem({
            field: 'nodes',
            modalBindings: {
                clusterNodeList: this.clusterNodeList,
                isEditing: false,
            },
        });
    }

    /**
     * Method to edit cluster node.
     */
    public updateClusterNode(clusterNode : ClusterNodeConfigItem): void {
        const index = this.clusterNodeList.config.indexOf(clusterNode);

        this.editChildMessageItem({
            field: 'nodes',
            messageItem: clusterNode,
            modalBindings: {
                clusterNodeList: this.clusterNodeList,
                isEditing: true,
                currentIndex: index,
            },
        });
    }

    /**
     * Method used to import lazy-loaded modal component.
     */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            ClusterModalComponent,
        } = await import(
            /* webpackChunkName: "cluster-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/cluster-modal/cluster-modal.component'
        );

        return ClusterModalComponent as Type<Component>;
    }

    /**
     * Load Cluster config
     * @override
     */
    protected loadConfig(): IBaseRequestPromise<ICluster> {
        const params = this.getLoadParams();
        const headers = this.getLoadHeaders_();
        const url = `/api/${this.objectName + (params.length ? `?${params.join('&')}` : '')}`;
        const requestConfig = {
            group: 'config',
            method: 'GET',
            headers,
            url,
        };

        this.cancelRequests('config');

        return this.request<ICluster>(requestConfig)
            .then(this.onConfigLoad_.bind(this));
    }

    /**
     * Method will load required Field on add or edit action.
     * @override
     */
    protected requiredFields(): string[] {
        return [
            'virtual_ip',
            'virtual_ip6',
        ];
    }
}
